import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DIRECTORY_UNAVAILABLE_MESSAGE, NEAR_ME_LIMIT, NEAR_ME_RADIUS_KM, findNearMe } from "../lib/nearMe";
import { nearestGrounds } from "../lib/distance";
import { loadDirectoryGrounds } from "../lib/directoryLoad";
import { narrowPlayedByTerm, railLetterIndex, sortByNameAZ, sortPlayedForRail } from "../lib/groundsNearYou";
import { newUi } from "../lib/featureFlags";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
import { useRetireTextSize } from "../context/RetireTextSizeContext";

const NEAR_ME_IDLE = { status: "idle", results: [], message: "", position: null };

/**
 * Issue #765 — the data half of "grounds near you".
 *
 * PR #858 review round 1 (F3): the directory/facilities reads are NO
 * LONGER unbounded. `/api/grounds` and `/api/grounds/facilities` with no
 * viewport are gone from this hook entirely — that pair firing at mount
 * was watched failing live (`mount-requests.json`) and is exactly the
 * unbounded-read regression #609/#643 exists to prevent. The map is now
 * the SHIPPED, viewport-bound one (`GroundsMap.jsx`'s own
 * `DirectoryMapSurface`, reused, not rebuilt), so this hook owns only:
 *
 *   - `GET /venues/played` — this screen's own played list, bounded by the
 *     player's own match history, never the whole directory.
 *   - a per-ground facilities read, `GET /grounds/:id/facilities`, for the
 *     facts line (`groundFactsLine`), which the shipped map's own
 *     VIEWPORT-bound facilities layer cannot answer for a ground currently
 *     off-screen.
 *   - near-me's own lazy, on-press directory read (`loadNearMeDirectory`),
 *     unchanged — `lib/nearMe.js`'s own privacy shape: never fetched until
 *     the button is pressed.
 *
 * PR #858 review round 3 (B1): the facilities read above USED to fire once
 * per DISTINCT played ground_id, unconditionally, inside `load()` — a
 * 41-request fan-out for a 41-ground player, watched failing live
 * (`on-mount-requests.json`/`off-mount-requests.json`), and it fired even
 * with the flag OFF, because only `isPlayer` gated the mount effect and
 * `on` (the flag) did not. Two fixes:
 *
 *   - the mount effect below is now gated on `on` itself — flag off means
 *     zero requests from this hook, full stop, because nothing that reads
 *     its output is ever mounted on that leg.
 *   - the facilities read is no longer part of `load()` at all. It is
 *     `ensureFacilities(ids)`, called by `GroundsNearYouScreen` for
 *     whichever played rows are actually REVEALED (its own
 *     `PLAYED_LIST_VISIBLE_CAP_LOCAL` window), so a mount asks for as many
 *     rows as are on screen, not as many as the player has ever played.
 *     Already-known and in-flight ids are never re-asked for.
 *
 * `term` is now an ARGUMENT, not state this hook owns — PR #858 F5: the
 * ONE search box narrows both this hook's played list AND (in
 * `GroundsMap.jsx`) the shipped directory's own search, so the shared `q`
 * state has to live where both readers already are.
 */
export function useGroundsNearYou({ api, isPlayer = true, isCoach = false, term = "" } = {}) {
  // The redrawn screen is for a player and, since #1288, a coach (PR #858 F6
  // kept an admin on the shipped page) — `GroundsMap.jsx` only mounts this
  // hook's caller for those two, but the chromeless-mount effect below is
  // guarded on the role too, defensively:
  // a hook is called unconditionally (Rules of Hooks), never gated on role.
  // #1288 — a coach gets the redrawn screen too (round-12 CoachGrounds), with
  // "Grounds you added" in place of the played list.
  const on = newUi("grounds") && (isPlayer || isCoach);
  const { setActive: setChromeless } = useChromelessOverride();
  const { setRetired: setTextSizeRetired } = useRetireTextSize();
  useEffect(() => {
    if (!on) return undefined;
    setChromeless(true);
    setTextSizeRetired(true);
    return () => {
      setChromeless(false);
      setTextSizeRetired(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on]);

  const [playedVenues, setPlayedVenues] = useState([]);
  const [playedFailed, setPlayedFailed] = useState(false);
  // #1288 — a coach's own grounds (`GET /grounds/mine`), never `/venues/played`.
  const [addedVenues, setAddedVenues] = useState([]);
  // `/grounds/mine` is capped (1,000); the header says "1,000+" past it.
  const [addedTruncated, setAddedTruncated] = useState(false);
  const [facilitiesByGroundId, setFacilitiesByGroundId] = useState(new Map());
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    // Issue #765 fix (watched failing live, journey 28's admin persona):
    // `GET /venues/played` requires a `player_id` for a non-player caller
    // (`resolveScope`) and this screen never supplies one — a non-player
    // caller genuinely has no played history, which is an EMPTY answer,
    // not a FAILED one, so the request is skipped rather than fired and
    // its inevitable 400 read as "couldn't load the grounds". Moot after
    // F6 (this hook's caller is a player or, since #1288, a coach), kept as
    // belt-and-braces for any other role.
    if (isCoach) {
      try {
        const r = await api.get("/grounds/mine");
        setAddedVenues(r.data?.grounds || []);
        setAddedTruncated(Boolean(r.data?.truncated));
        setPlayedFailed(false);
      } catch {
        setAddedVenues([]);
        setPlayedFailed(true);
      } finally {
        setLoaded(true);
      }
      return;
    }
    if (!isPlayer) {
      setPlayedVenues([]);
      setPlayedFailed(false);
      setFacilitiesByGroundId(new Map());
      setLoaded(true);
      return;
    }
    try {
      const r = await api.get("/venues/played");
      const rows = r.data || [];
      setPlayedVenues(rows);
      setPlayedFailed(false);
    } catch {
      setPlayedVenues([]);
      setPlayedFailed(true);
    } finally {
      setLoaded(true);
    }
  }, [api, isPlayer, isCoach]);

  useEffect(() => {
    if (!on) {
      // B1: flag off means this hook's caller never mounts anything that
      // reads its output (`GroundsMap.jsx` only renders `GroundsNearYouScreen`
      // on the `on` leg) — so `/venues/played` firing here anyway was pure
      // waste, watched failing live on `off-mount-requests.json`. Zero
      // requests, full stop.
      setLoaded(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      if (!cancelled) await load();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on]);

  // PR #858 review round 3 (B1): the per-ground facilities read, pulled out
  // of `load()` so it is no longer an unconditional fan-out over every
  // played ground. The caller (`GroundsNearYouScreen`) asks for exactly the
  // ids currently REVEALED; ids already known or already in flight are
  // skipped, so scrolling/"more"/a rail jump that reveals more rows asks
  // for only the newly-revealed ones.
  const inFlightFacilityIds = useRef(new Set());
  const ensureFacilities = useCallback(
    (ids) => {
      const want = (ids || []).filter(
        (id) => id && !facilitiesByGroundId.has(id) && !inFlightFacilityIds.current.has(id),
      );
      if (want.length === 0) return;
      want.forEach((id) => inFlightFacilityIds.current.add(id));
      Promise.all(
        want.map((id) =>
          api
            .get(`/grounds/${id}/facilities`)
            .then((res) => [id, res.data || []])
            .catch(() => [id, []]),
        ),
      ).then((pairs) => {
        want.forEach((id) => inFlightFacilityIds.current.delete(id));
        setFacilitiesByGroundId((prev) => {
          const next = new Map(prev);
          pairs.forEach(([id, rows]) => next.set(id, rows));
          return next;
        });
      });
    },
    [api, facilitiesByGroundId],
  );

  /** The played list, A-Z. PR #858 F11: a row with no `ground_id` (legacy
   * free text) keeps its place — `ground_id: null`, no facilities, no
   * card link — rather than disappearing from the list or the count. */
  const playedAll = useMemo(() => {
    const rows = playedVenues.map((v) => ({
      ground_id: v.ground_id ?? null,
      name: v.ground,
      city: v.city ?? null,
      country: v.country ?? null,
      matches: Number(v.matches) || 0,
      latitude: v.latitude ?? null,
      longitude: v.longitude ?? null,
      placeType: v.place_type ?? null,
      facilities: v.ground_id ? facilitiesByGroundId.get(v.ground_id) || [] : [],
      // Issue #683's own rule: a venue the player PLAYED keeps showing
      // even once archived — `/venues/played` has always carried
      // `archived_at` through for exactly this.
      archivedAt: v.archived_at ?? null,
    }));
    // D165 — A-Z first, then a name starting with a digit or symbol,
    // sorted at the end (never a "#" group) — see `sortPlayedForRail`'s
    // own header.
    return sortPlayedForRail(rows);
  }, [playedVenues, facilitiesByGroundId]);

  const playedNarrowed = useMemo(() => narrowPlayedByTerm(playedAll, term), [playedAll, term]);

  /** #1288 — the coach's grounds, A-Z, with no window and no rail. */
  const addedAll = useMemo(
    () =>
      sortByNameAZ(
        addedVenues.map((g) => ({
          ground_id: g.ground_id,
          name: g.name,
          city: g.city ?? null,
          country: g.country ?? null,
          latitude: g.latitude ?? null,
          longitude: g.longitude ?? null,
          placeType: g.place_type ?? null,
          facilities: facilitiesByGroundId.get(g.ground_id) || [],
          archivedAt: g.archived_at ?? null,
        })),
      ),
    [addedVenues, facilitiesByGroundId],
  );
  const addedNarrowed = useMemo(() => narrowPlayedByTerm(addedAll, term), [addedAll, term]);

  // ---------- "Use my location" — issue #134's privacy shape, unchanged
  // by this fix round: a lazy, on-press-only directory read, independent
  // of the shipped map's own viewport-bound one. ----------
  const [nearMe, setNearMe] = useState(NEAR_ME_IDLE);
  const [nearMeDirectory, setNearMeDirectory] = useState(null);
  const [nearbyFacilitiesByGroundId, setNearbyFacilitiesByGroundId] = useState(new Map());

  const loadNearMeDirectory = useCallback(async () => {
    if (nearMeDirectory) return nearMeDirectory;
    const dir = await loadDirectoryGrounds(api, undefined, false);
    const grounds = dir.ok ? dir.grounds || [] : null;
    if (dir.ok) setNearMeDirectory(grounds);
    return grounds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, nearMeDirectory]);

  const useMyLocation = useCallback(async () => {
    setNearMe({ ...NEAR_ME_IDLE, status: "locating" });
    const directory = await loadNearMeDirectory();
    if (directory === null) {
      setNearMe({ ...NEAR_ME_IDLE, status: "error", message: DIRECTORY_UNAVAILABLE_MESSAGE });
      return;
    }
    const outcome = await findNearMe(directory, { maxKm: NEAR_ME_RADIUS_KM });
    if (!outcome.ok) {
      setNearMe({ ...NEAR_ME_IDLE, status: "error", message: outcome.message });
      return;
    }
    setNearMe({ status: "ready", results: outcome.results, message: "", position: outcome.position });
    // Bounded by the near-me result set (capped, typically small), not the
    // world — the same reasoning as the played list's own per-ground reads
    // above, for the facts chips a Nearby row draws.
    const ids = [...new Set(outcome.results.map((g) => g.ground_id).filter(Boolean))];
    const pairs = await Promise.all(
      ids.map((id) =>
        api
          .get(`/grounds/${id}/facilities`)
          .then((res) => [id, res.data || []])
          .catch(() => [id, []]),
      ),
    );
    setNearbyFacilitiesByGroundId(new Map(pairs));
  }, [api, loadNearMeDirectory]);

  const clearNearMe = useCallback(() => setNearMe(NEAR_ME_IDLE), []);

  // #1143 part 2: the grounds added in this visit, which the Nearby row marks
  // New. Nothing persists it, so a reload clears it; `load` never touches it.
  const [newGroundIds, setNewGroundIds] = useState(() => new Set());

  /**
   * #1143 — a ground the player has just added, folded into the near-me
   * answer from the row the server returned. No request: the add step's own
   * POST is the only one, and the directory the near-me press already read
   * is patched rather than re-read.
   *
   *   - the cached directory gains the row, so a LATER press ranks it;
   *   - a "ready" answer re-ranks `top-N + the new row`, which is the same
   *     top N as ranking the whole directory plus the row;
   *   - an idle answer is left alone, and with no cached directory the next
   *     press reads a fresh one that already holds the ground.
   * The played list is not touched: it is grounds with a match, and a ground
   * nobody has played yet does not belong in it.
   */
  const addGround = useCallback((ground) => {
    if (!ground || !ground.ground_id) return;
    setNewGroundIds((prev) => (prev.has(ground.ground_id) ? prev : new Set(prev).add(ground.ground_id)));
    // #1288: a coach's new ground joins "Grounds you added" (a player's does not
    // join the played list: nobody has played it). "Use this ground" on a
    // duplicate hands back a ground someone else added: `is_mine` keeps it out.
    if (isCoach && ground.is_mine) setAddedVenues((prev) => (prev.some((g) => g.ground_id === ground.ground_id) ? prev : [...prev, ground]));
    const holds = (rows) => rows.some((g) => g.ground_id === ground.ground_id);
    setNearMeDirectory((prev) => (prev && !holds(prev) ? [...prev, ground] : prev));
    setNearMe((prev) => {
      if (prev.status !== "ready" || holds(prev.results)) return prev;
      const results = nearestGrounds(prev.position, [...prev.results, ground], {
        limit: NEAR_ME_LIMIT,
        maxKm: NEAR_ME_RADIUS_KM,
      });
      return { ...prev, results };
    });
  }, [isCoach]);

  const nearbyList = useMemo(
    () =>
      nearMe.status === "ready"
        ? nearMe.results.map((g) => ({ ...g, facilities: nearbyFacilitiesByGroundId.get(g.ground_id) || [] }))
        : [],
    [nearMe, nearbyFacilitiesByGroundId],
  );

  // D165 — the rail indexes every row the list shows, played AND nearby
  // (review round 3, F4): computed here rather than beside `playedAll`
  // above because `nearbyList` (below) has to exist first. The rail dims/
  // enables against the WHOLE narrowed played list, not just whatever
  // window is currently revealed (PR #858 F1/F2 — the rail "always points
  // somewhere" across the full answer) union the WHOLE nearby list
  // (never windowed at all).
  const playedRail = useMemo(() => railLetterIndex(playedNarrowed, nearbyList), [playedNarrowed, nearbyList]);

  return {
    loaded,
    playedFailed,
    contentFailed: playedFailed,
    playedAll,
    playedNarrowed,
    playedRail,
    addedAll,
    addedNarrowed,
    addedTruncated,
    nearMe,
    useMyLocation,
    clearNearMe,
    nearbyList,
    newGroundIds,
    addGround,
    reload: load,
    ensureFacilities,
  };
}

export default useGroundsNearYou;
